// Sandbox host tunnel: the protocol and wire contract between the Goblic server (API + worker) and the Go agent
// (docs/plans/2026-10-04-sandbox-host-installer.md §5-§9, §13). Everything the packages must agree on lives here:
// names, codes, header names, paths, patterns, intervals, limits, message schemas. tunnel-contract.json holds the
// same values machine-readably (built by buildTunnelContract(), compared in tunnel.test.ts) so the Go constants
// can be tested against it. Browser-safe (zod only).
import { z } from 'zod'

// ---------------------------------------------------------------------------
// Versions, identity scheme, statuses
// ---------------------------------------------------------------------------

/** Protocol major. The server accepts agents with the same major (§6.6). */
export const TUNNEL_PROTOCOL_VERSION = 1
/** Every protocol path carries `X-Goblic-Auth-Scheme: bearer` so a keypair scheme can follow without breaking (§0 #17). */
export const TUNNEL_AUTH_SCHEME = 'bearer'

export const AGENT_APPROVALS = ['pending', 'approved'] as const
/** sandbox_hosts.agent_approval (null = not enrolled). */
export type AgentApproval = (typeof AGENT_APPROVALS)[number]

/** sandbox_hosts.agent_update_state (null = no running or failed update, §9.2). */
export const AGENT_UPDATE_STATES = ['requested', 'updating', 'failed'] as const
export type AgentUpdateState = (typeof AGENT_UPDATE_STATES)[number]

/** `hello.update.state` (§6.2). */
export const AGENT_HELLO_UPDATE_STATES = ['none', 'committed', 'rolled-back'] as const
export type AgentHelloUpdateState = (typeof AGENT_HELLO_UPDATE_STATES)[number]

export const AGENT_UPDATE_REASONS = ['auto', 'manual'] as const
export type AgentUpdateReason = (typeof AGENT_UPDATE_REASONS)[number]

export const AGENT_ARCHES = ['amd64', 'arm64'] as const
export type AgentArch = (typeof AGENT_ARCHES)[number]

/** Presenter `agent.state` (§1). */
export const AGENT_STATES = ['waiting', 'pending', 'connected', 'offline'] as const
export type AgentState = (typeof AGENT_STATES)[number]

/** Presenter `agent.update.state` (§7.2). */
export const AGENT_PRESENTED_UPDATE_STATES = ['current', 'update-available', 'requested', 'updating', 'failed'] as const
export type AgentPresentedUpdateState = (typeof AGENT_PRESENTED_UPDATE_STATES)[number]

/** Request classes of the Docker API through the tunnel (§6.3.2). */
export const TUNNEL_STREAM_CLASSES = ['control', 'bulk', 'stream'] as const
export type TunnelStreamClass = (typeof TUNNEL_STREAM_CLASSES)[number]

// ---------------------------------------------------------------------------
// Close codes (§6.5)
// ---------------------------------------------------------------------------

export const TUNNEL_CLOSE_CODES = {
  /** Normal close. */
  normal: 1000,
  /** Going away: worker stop or agent restart. */
  goingAway: 1001,
  /** Stream error. */
  streamError: 1011,
  /** Credential revoked or invalid. */
  revoked: 4001,
  /** Unknown, expired or foreign stream id. */
  unknownStream: 4004,
  /** Ping time-out. */
  pingTimeout: 4008,
  /** A control connection for this host is already live. */
  alreadyConnected: 4009,
  /** Protocol too old. */
  protocolTooOld: 4010,
  /** Host disabled or removed. */
  hostDisabled: 4011,
  /** Abuse or limit exceeded. */
  abuse: 4012,
  /** hello missing or invalid. */
  invalidHello: 4014,
} as const
export type TunnelCloseCodeName = keyof typeof TUNNEL_CLOSE_CODES
export type TunnelCloseCode = (typeof TUNNEL_CLOSE_CODES)[TunnelCloseCodeName]

// ---------------------------------------------------------------------------
// Headers, paths, files (§6.1, §7.2, §8.4)
// ---------------------------------------------------------------------------

export const TUNNEL_HEADERS = {
  authorization: 'Authorization',
  host: 'X-Goblic-Host',
  protocol: 'X-Goblic-Protocol',
  authScheme: 'X-Goblic-Auth-Scheme',
  agentVersion: 'X-Goblic-Agent-Version',
  /** Set by nginx, never by the agent (§5.4). */
  edge: 'X-Goblic-Edge',
  /** Response header of GET /install/agent/:version/:arch. */
  agentSha256: 'X-Goblic-Agent-Sha256',
} as const

export const TUNNEL_PATHS = {
  control: '/agent/control',
  /** `/agent/stream/<id>` with a 32-hex id. */
  streamPrefix: '/agent/stream/',
  ping: '/install/ping',
  manifest: '/install/agent/manifest.json',
  register: '/install/register',
  /** `/install/agent/<version>/<arch>`. */
  assetPrefix: '/install/agent/',
  /** `/install/<token>`. */
  scriptPrefix: '/install/',
} as const

/** Fixed in the agent's code; never part of a message (§5.3). */
export const AGENT_DOCKER_SOCKET = '/var/run/docker.sock'

export const AGENT_HOST_PATHS = {
  binary: '/usr/local/bin/goblic-agent',
  binaryOld: '/usr/local/bin/goblic-agent.old',
  binaryNew: '/usr/local/bin/goblic-agent.new',
  configDir: '/etc/goblic-agent',
  env: '/etc/goblic-agent/agent.env',
  stateDir: '/var/lib/goblic-agent',
  state: '/var/lib/goblic-agent/state.json',
  update: '/var/lib/goblic-agent/update.json',
  unit: '/etc/systemd/system/goblic-agent.service',
  service: 'goblic-agent.service',
} as const

/** Keys of /etc/goblic-agent/agent.env. */
export const AGENT_ENV_KEYS = {
  origin: 'GOBLIC_ORIGIN',
  hostId: 'GOBLIC_HOST_ID',
  secret: 'GOBLIC_AGENT_SECRET',
  installId: 'GOBLIC_INSTALL_ID',
  allowInsecure: 'GOBLIC_ALLOW_INSECURE',
  httpsProxy: 'HTTPS_PROXY',
  noProxy: 'NO_PROXY',
} as const

// ---------------------------------------------------------------------------
// Token formats (§2.1, §5.1)
// ---------------------------------------------------------------------------

export const INSTALL_TOKEN_PREFIX = 'gbi_'
export const INSTALL_NONCE_PREFIX = 'gbn_'
export const AGENT_SECRET_PREFIX = 'gba_'

const B64URL_43 = '[A-Za-z0-9_-]{43}'

/** Regex sources (strings) so the JSON contract can carry them; the RegExp constants below are built from these. */
export const TUNNEL_PATTERNS = {
  installToken: `^gbi_${B64URL_43}$`,
  nonce: `^gbn_${B64URL_43}$`,
  secret: `^gba_${B64URL_43}$`,
  bearerHeader: `^Bearer gba_${B64URL_43}$`,
  streamId: '^[0-9a-f]{32}$',
  hostId: '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$',
  protocolHeader: '^[0-9]{1,3}$',
  /** Header value and the format check on agentVersion / release.version. */
  agentVersion: '^[0-9A-Za-z.+-]{1,64}$',
  /** What build.sh produces: `<VERSION>-g<8 hex>` (§8.1). */
  agentBuildVersion: '^[0-9]+\\.[0-9]+\\.[0-9]+-g[0-9a-f]{8}$',
  sha256: '^[0-9a-f]{64}$',
  /** Renderer validation of `__ORIGIN__` (§3). */
  origin: '^https?://[A-Za-z0-9.-]+(:[0-9]{1,5})?$',
} as const

export const INSTALL_TOKEN_REGEX = new RegExp(TUNNEL_PATTERNS.installToken)
export const INSTALL_NONCE_REGEX = new RegExp(TUNNEL_PATTERNS.nonce)
export const AGENT_SECRET_REGEX = new RegExp(TUNNEL_PATTERNS.secret)
export const AGENT_BEARER_HEADER_REGEX = new RegExp(TUNNEL_PATTERNS.bearerHeader)
export const TUNNEL_STREAM_ID_REGEX = new RegExp(TUNNEL_PATTERNS.streamId)
export const TUNNEL_HOST_ID_REGEX = new RegExp(TUNNEL_PATTERNS.hostId)
export const TUNNEL_PROTOCOL_HEADER_REGEX = new RegExp(TUNNEL_PATTERNS.protocolHeader)
export const AGENT_VERSION_REGEX = new RegExp(TUNNEL_PATTERNS.agentVersion)
export const AGENT_BUILD_VERSION_REGEX = new RegExp(TUNNEL_PATTERNS.agentBuildVersion)
export const SHA256_HEX_REGEX = new RegExp(TUNNEL_PATTERNS.sha256)
export const INSTALL_ORIGIN_REGEX = new RegExp(TUNNEL_PATTERNS.origin)

// ---------------------------------------------------------------------------
// Intervals and limits (ms unless stated) (§4, §5, §6, §9)
// ---------------------------------------------------------------------------

export const TUNNEL_INTERVALS = {
  /** Install token validity, fixed (§0 #12). */
  tokenTtlMs: 3_600_000,
  /** Registration nonce validity from the GET, never beyond the token (§5.1). */
  nonceTtlMs: 1_800_000,
  /** Pending data-socket (`open`) time-out (§6.3). */
  streamPendingMs: 30_000,
  /** Agent dial time-out of the data WebSocket (§6.3). */
  dataDialMs: 10_000,
  /** The agent must send hello within this after welcome (§6.2). */
  helloMs: 10_000,
  heartbeatMs: 20_000,
  controlPingMs: 20_000,
  dataPingMs: 30_000,
  /** Server: 2 missed pongs (§6.4). */
  controlDeadMs: 45_000,
  dataDeadMs: 75_000,
  /** The agent's own control-liveness limit and ping time-out (§6.4). */
  agentControlSilenceMs: 45_000,
  agentDataSilenceMs: 75_000,
  agentPingTimeoutMs: 10_000,
  /** Agent `status` cadence; the broker writes agent_last_seen_at at this rate (§6.2). */
  statusMs: 30_000,
  /** Duplicate control: wait for the old connection's pong (§6.5). */
  duplicateProbeMs: 5_000,
  /** agent.connected when last seen is younger than this (§1). */
  connectedWindowMs: 90_000,
  /** Broker re-reads connected hosts (§5.5); pending connections more often. */
  brokerPollMs: 30_000,
  brokerPollPendingMs: 5_000,
  reconnectMinMs: 1_000,
  reconnectMaxMs: 60_000,
  /** Backoff counter resets after this long a stable connection. */
  reconnectStableMs: 60_000,
  /** Agent waits this long on 403 `disabled` / 4011. */
  disabledRetryMs: 300_000,
  /** After three consecutive 401/4001 the agent retries once per hour. */
  revokedRetryMs: 3_600_000,
  /** openStream waits for an updating agent's reconnect (§7.1). */
  updateReconnectWaitMs: 90_000,
  /** Worker updater cadence and idle requirement (§9.2). */
  updaterTickMs: 30_000,
  updateIdleMs: 20_000,
  /** `updating` without hello for this long becomes `failed` (§9.2). */
  updateStuckMs: 300_000,
  /** Damping after a failed auto-update (§9.2, §9.3). */
  updateDampingMs: 86_400_000,
  /** Agent update: watchdog, commit and boot attempts (§9.3). */
  updateWatchdogMs: 90_000,
  updateCommitMs: 10_000,
  updateMaxBootAttempts: 3,
  /** Client-IP block after repeated 4012 (§5.4 point 6). */
  abuseBlockMs: 900_000,
} as const

export const TUNNEL_LIMITS = {
  /** Total open/pending streams per host (= welcome.maxStreams). */
  maxStreams: 64,
  /** Reserved for class `control`; stream+bulk together may use maxStreams - reservedControlStreams. */
  reservedControlStreams: 8,
  /** WebSocket maxPayload per frame. */
  maxFramePayloadBytes: 4_194_304,
  /** Control messages. */
  maxControlMessageBytes: 65_536,
  /** Chunks above this are split into frames of this size (§6.3.1). */
  maxChunkBytes: 1_048_576,
  /** Response caps per request class (§6.3.2). */
  controlResponseMaxBytes: 16_777_216,
  bulkResponseMaxBytes: 4_294_967_296,
  streamResponseMaxBytes: 1_073_741_824,
  jsonLineMaxBytes: 1_048_576,
  /** POST /install/register body. */
  registerBodyMaxBytes: 16_384,
  /** Agent update download (§9.3 step 2). */
  updateDownloadMaxBytes: 67_108_864,
  updateDownloadTimeoutMs: 300_000,
  updateDownloadAttempts: 5,
  /** 4012 count within an hour that blocks the host (§5.4 point 6). */
  abuseCloseCountPerHour: 3,
  /** Consecutive 401/4001 before the agent stops actively retrying. */
  revokedAttemptsBeforeStop: 3,
  /** Worker listener (§5.4), per minute unless noted. */
  upgradesPerIpPerMinute: 30,
  failedAuthPerIpPerMinute: 10,
  failedAuthPerHostPerMinute: 20,
  incompleteUpgradesTotal: 200,
  incompleteUpgradesPerIp: 20,
  controlAttemptsPerHostPerMinute: 30,
  unknownStreamIdsPerHostPerMinute: 5,
  openRatePerSecond: 20,
  openBurst: 40,
  ingressBytesPerSecond: 67_108_864,
} as const

/** Default for GOBLIC_TUNNEL_STREAM_MAX_BYTES and the worker listener port. */
export const TUNNEL_DEFAULT_PORT = 3100
/** Installer script version shipped in `facts.scriptVersion`. */
export const INSTALL_SCRIPT_VERSION = 1

// ---------------------------------------------------------------------------
// Install command (§2.1): exact strings
// ---------------------------------------------------------------------------

/** The one-line command. `insecureHttp` is the test profile only (GOBLIC_INSTALL_ALLOW_HTTP=1, http origin). */
export function buildInstallCommand(origin: string, token: string, opts: { insecureHttp?: boolean } = {}): string {
  return opts.insecureHttp
    ? `curl -fsSL http://${origin.replace(/^https?:\/\//, '')}/install/${token} | sudo sh`
    : `curl --proto '=https' --tlsv1.2 -fsSL https://${origin.replace(/^https?:\/\//, '')}/install/${token} | sudo sh`
}

// ---------------------------------------------------------------------------
// Shared schema pieces
// ---------------------------------------------------------------------------

const HostIdSchema = z.string().regex(TUNNEL_HOST_ID_REGEX)
const Sha256Schema = z.string().regex(SHA256_HEX_REGEX)
const AgentVersionSchema = z.string().regex(AGENT_VERSION_REGEX)
/** Free text from a host ends up in logs and the UI: control characters (newlines in particular) are stripped. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/g
const freeText = (max: number) => z.string().max(max).transform((value) => value.replace(CONTROL_CHARS, ' ').trim())
const nonEmptyFreeText = (max: number) => freeText(max).refine((value) => value.length > 0, 'must not be empty')
const ArchSchema = z.enum(AGENT_ARCHES)
const StreamIdSchema = z.string().regex(TUNNEL_STREAM_ID_REGEX)

/** An agent release as the server serves it (installation_settings.agent_release, welcome.release, manifest) (§7.2, §13). */
export const AgentReleaseSchema = z.object({
  version: AgentVersionSchema,
  protocol: z.number().int().positive(),
  sha256: z.object({ amd64: Sha256Schema, arm64: Sha256Schema }),
})
export type AgentRelease = z.infer<typeof AgentReleaseSchema>

/** The release as sent in `welcome` / `release` messages (no protocol field, §6.2). */
export const AgentReleaseWireSchema = z.object({
  version: AgentVersionSchema,
  sha256: z.object({ amd64: Sha256Schema, arm64: Sha256Schema }),
})
export type AgentReleaseWire = z.infer<typeof AgentReleaseWireSchema>

/** GET /install/agent/manifest.json (§7.2). */
export const AgentManifestSchema = z.object({
  version: AgentVersionSchema,
  protocol: z.number().int().positive(),
  binaries: z.object({
    amd64: z.object({ file: z.string().min(1).max(128), sha256: Sha256Schema, size: z.number().int().nonnegative() }),
    arm64: z.object({ file: z.string().min(1).max(128), sha256: Sha256Schema, size: z.number().int().nonnegative() }),
  }),
})
export type AgentManifest = z.infer<typeof AgentManifestSchema>

// ---------------------------------------------------------------------------
// Register (§7.2)
// ---------------------------------------------------------------------------

/** `facts` of POST /install/register. The same caps apply to hello (§6.2). */
export const AgentRegisterFactsSchema = z.object({
  hostname: nonEmptyFreeText(253),
  os: freeText(128),
  kernel: freeText(128),
  arch: ArchSchema,
  cpus: z.number().int().min(1).max(4096),
  memoryBytes: z.number().int().nonnegative(),
  dockerVersion: freeText(64),
  dockerApiVersion: freeText(16),
  agentVersion: AgentVersionSchema,
  scriptVersion: z.number().int().nonnegative(),
  insecure: z.boolean(),
})
export type AgentRegisterFacts = z.infer<typeof AgentRegisterFactsSchema>

export const AgentRegisterBodySchema = z.object({
  hostId: HostIdSchema,
  nonce: z.string().regex(INSTALL_NONCE_REGEX),
  facts: AgentRegisterFactsSchema,
})
export type AgentRegisterBody = z.infer<typeof AgentRegisterBodySchema>

export const AgentRegisterResponseSchema = z.object({
  hostId: HostIdSchema,
  hostName: z.string(),
  secret: z.string().regex(AGENT_SECRET_REGEX),
  origin: z.string().regex(INSTALL_ORIGIN_REGEX),
  installId: z.string().uuid(),
  approval: z.literal('pending'),
  protocol: z.literal(TUNNEL_PROTOCOL_VERSION),
})
export type AgentRegisterResponse = z.infer<typeof AgentRegisterResponseSchema>

/**
 * What sandbox_hosts.agent_facts holds: the register facts, later merged with the agent's hello. dockerVersion is ''
 * while the daemon is unreachable. Fields only one of the two sources provides are optional.
 */
export interface AgentFacts {
  hostname: string
  os: string
  kernel: string
  arch: AgentArch
  cpus: number
  memoryBytes: number
  dockerVersion: string
  dockerApiVersion?: string
  agentVersion: string
  scriptVersion?: number
  insecure?: boolean
}

// ---------------------------------------------------------------------------
// Control messages (§6.2)
// ---------------------------------------------------------------------------

export const TUNNEL_SERVER_MESSAGE_TYPES = ['welcome', 'approved', 'open', 'release', 'update', 'bye'] as const
export const TUNNEL_AGENT_MESSAGE_TYPES = ['hello', 'status', 'open-failed', 'update-started', 'update-deferred', 'update-failed'] as const
/** Text frame on a data socket: the sender closed its direction (§6.3). */
export const TUNNEL_EOF_FRAME = 'eof'
/** `open-failed.error` of an agent that is updating (§9.3). */
export const TUNNEL_ERROR_UPDATING = 'updating'

export const WelcomeMessageSchema = z.object({
  type: z.literal('welcome'),
  protocol: z.number().int().positive(),
  heartbeatMs: z.number().int().positive(),
  maxStreams: z.number().int().positive(),
  serverVersion: z.string().max(64),
  approval: z.enum(AGENT_APPROVALS),
  hostName: z.string(),
  installId: z.string().uuid(),
  autoUpdate: z.boolean(),
  release: AgentReleaseWireSchema.nullable(),
})
export type WelcomeMessage = z.infer<typeof WelcomeMessageSchema>

export const ApprovedMessageSchema = z.object({ type: z.literal('approved') })
export type ApprovedMessage = z.infer<typeof ApprovedMessageSchema>

export const OpenMessageSchema = z.object({ type: z.literal('open'), id: StreamIdSchema, v: z.number().int().positive() })
export type OpenMessage = z.infer<typeof OpenMessageSchema>

export const ReleaseMessageSchema = z.object({
  type: z.literal('release'),
  autoUpdate: z.boolean(),
  release: AgentReleaseWireSchema.nullable(),
})
export type ReleaseMessage = z.infer<typeof ReleaseMessageSchema>

export const UpdateMessageSchema = z.object({
  type: z.literal('update'),
  version: AgentVersionSchema,
  /** The sha256 of this agent's arch. */
  sha256: Sha256Schema,
  reason: z.enum(AGENT_UPDATE_REASONS),
})
export type UpdateMessage = z.infer<typeof UpdateMessageSchema>

export const ByeMessageSchema = z.object({ type: z.literal('bye'), code: z.number().int(), reason: z.string().max(256) })
export type ByeMessage = z.infer<typeof ByeMessageSchema>

export const TunnelServerMessageSchema = z.discriminatedUnion('type', [
  WelcomeMessageSchema,
  ApprovedMessageSchema,
  OpenMessageSchema,
  ReleaseMessageSchema,
  UpdateMessageSchema,
  ByeMessageSchema,
])
export type TunnelServerMessage = z.infer<typeof TunnelServerMessageSchema>

/** Length caps are the contract of §6.2; a hello outside them is closed with 4014. Unknown fields are stripped. */
export const HelloMessageSchema = z.object({
  type: z.literal('hello'),
  protocol: z.number().int().positive(),
  agentVersion: AgentVersionSchema,
  arch: ArchSchema,
  os: freeText(128),
  kernel: freeText(128),
  hostname: nonEmptyFreeText(253),
  cpus: z.number().int().min(1).max(4096),
  memoryBytes: z.number().int().nonnegative(),
  docker: z.object({ version: freeText(64), apiVersion: freeText(16) }).nullable(),
  update: z.object({
    state: z.enum(AGENT_HELLO_UPDATE_STATES),
    from: freeText(64).nullable(),
    to: freeText(64).nullable(),
    error: freeText(512).nullable(),
  }),
})
export type HelloMessage = z.infer<typeof HelloMessageSchema>

export const StatusMessageSchema = z.object({
  type: z.literal('status'),
  activeStreams: z.number().int().nonnegative(),
  dockerOk: z.boolean(),
})
export type StatusMessage = z.infer<typeof StatusMessageSchema>

export const OpenFailedMessageSchema = z.object({ type: z.literal('open-failed'), id: StreamIdSchema, error: freeText(512) })
export type OpenFailedMessage = z.infer<typeof OpenFailedMessageSchema>

export const UpdateStartedMessageSchema = z.object({ type: z.literal('update-started'), version: AgentVersionSchema })
export type UpdateStartedMessage = z.infer<typeof UpdateStartedMessageSchema>

export const UpdateDeferredMessageSchema = z.object({ type: z.literal('update-deferred'), reason: z.enum(['busy', 'damped']) })
export type UpdateDeferredMessage = z.infer<typeof UpdateDeferredMessageSchema>

export const UpdateFailedMessageSchema = z.object({ type: z.literal('update-failed'), version: AgentVersionSchema, error: freeText(512) })
export type UpdateFailedMessage = z.infer<typeof UpdateFailedMessageSchema>

export const TunnelAgentMessageSchema = z.discriminatedUnion('type', [
  HelloMessageSchema,
  StatusMessageSchema,
  OpenFailedMessageSchema,
  UpdateStartedMessageSchema,
  UpdateDeferredMessageSchema,
  UpdateFailedMessageSchema,
])
export type TunnelAgentMessage = z.infer<typeof TunnelAgentMessageSchema>

/**
 * Parses one control text frame. Unknown `type` is not an error (forward compatibility, §6.2): the caller ignores it.
 * Known type with a bad shape is `invalid` (hello: close 4014).
 */
export type ParsedControlMessage<T> =
  | { ok: true; message: T }
  | { ok: false; reason: 'not-json' | 'not-an-object' | 'unknown-type' | 'invalid'; error?: string }

function parseControl<T>(raw: string, types: readonly string[], schema: z.ZodType<T>): ParsedControlMessage<T> {
  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch {
    return { ok: false, reason: 'not-json' }
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return { ok: false, reason: 'not-an-object' }
  const type = (value as { type?: unknown }).type
  if (typeof type !== 'string' || !types.includes(type)) return { ok: false, reason: 'unknown-type' }
  const parsed = schema.safeParse(value)
  return parsed.success ? { ok: true, message: parsed.data } : { ok: false, reason: 'invalid', error: parsed.error.message }
}

export function parseServerMessage(raw: string): ParsedControlMessage<TunnelServerMessage> {
  return parseControl(raw, TUNNEL_SERVER_MESSAGE_TYPES, TunnelServerMessageSchema)
}
export function parseAgentMessage(raw: string): ParsedControlMessage<TunnelAgentMessage> {
  return parseControl(raw, TUNNEL_AGENT_MESSAGE_TYPES, TunnelAgentMessageSchema)
}

// ---------------------------------------------------------------------------
// pg-boss: API -> worker (§5.5, §7.2)
// ---------------------------------------------------------------------------

/** pg-boss queue: "re-read this tunnel host's row now". Idempotent. */
export const SANDBOX_HOST_AGENT_CHANGED_QUEUE = 'sandbox-host.agent-changed'
/** Same options on both sides (createQueue on API and worker). */
export const SANDBOX_HOST_AGENT_CHANGED_QUEUE_OPTIONS = { retryLimit: 0, expireInSeconds: 120 } as const
export const SANDBOX_HOST_AGENT_CHANGED_REASONS = ['approved', 'revoked', 'removed', 'disabled', 'secret-replaced', 'update-now'] as const
export type SandboxHostAgentChangedReason = (typeof SANDBOX_HOST_AGENT_CHANGED_REASONS)[number]
export interface SandboxHostAgentChangedJob {
  hostId: string
  reason: SandboxHostAgentChangedReason
}

// ---------------------------------------------------------------------------
// Admin API contract (§7.2)
// ---------------------------------------------------------------------------

/** Error codes the install flow answers with (`{ error }`). */
export const INSTALL_ERROR_CODES = {
  tunnelNotConfigured: 'tunnel_not_configured',
  publicUrlNotConfigured: 'public_url_not_configured',
  agentBinariesUnavailable: 'agent_binaries_unavailable',
  invalidRegistration: 'invalid_registration',
  enrollmentChanged: 'enrollment_changed',
} as const

/** POST /installation/sandbox-hosts/:id/approve-agent. `enrolledAt` is the `agent.enrolledAt` of the card the admin saw. */
export const ApproveAgentBodySchema = z.object({ enrolledAt: z.string().datetime() }).strict()
export type ApproveAgentBody = z.infer<typeof ApproveAgentBodySchema>

/** 409 body of approve-agent when the host is no longer pending or was enrolled again. */
export const EnrollmentChangedResponseSchema = z.object({ error: z.literal(INSTALL_ERROR_CODES.enrollmentChanged) })
export type EnrollmentChangedResponse = z.infer<typeof EnrollmentChangedResponseSchema>

/** `install` of the create response and the response of POST …/install-token. The token only lives inside `command`. */
export interface SandboxHostInstall {
  command: string
  expiresAt: string
}

/** GET/PUT /installation/agent-policy. */
export interface AgentPolicy {
  autoUpdate: boolean
  release: { version: string; protocol: number } | null
  binariesAvailable: boolean
  tunnelConfigured: boolean
  publicUrl: string | null
}
export const UpdateAgentPolicyBodySchema = z.object({ autoUpdate: z.boolean() }).strict()
export type UpdateAgentPolicyBody = z.infer<typeof UpdateAgentPolicyBodySchema>

/** `agent` block of SandboxHost (§7.2); null for non-tunnel hosts. Never carries hashes, tokens or nonces. */
export interface SandboxHostAgent {
  state: AgentState
  approval: AgentApproval | null
  /** lastSeenAt is younger than TUNNEL_INTERVALS.connectedWindowMs. */
  connected: boolean
  version: string | null
  lastSeenAt: string | null
  enrolledAt: string | null
  enrolledIp: string | null
  remoteIp: string | null
  remoteIpChangedAt: string | null
  tokenExpiresAt: string | null
  script: { fetchCount: number; firstFetchedAt: string | null; lastFetchedAt: string | null; lastFetchIp: string | null }
  facts: {
    hostname: string
    os: string
    kernel: string
    arch: string
    cpus: number
    memoryBytes: number
    dockerVersion: string
    agentVersion: string
  } | null
  update: { state: AgentPresentedUpdateState; targetVersion: string | null; error: string | null }
}

// ---------------------------------------------------------------------------
// Service interfaces other packages fill (§7.1, §7.2)
// ---------------------------------------------------------------------------

/**
 * Worker-side broker (implemented in packages/worker/src/tunnel/broker.ts). Generic over the socket type because the
 * concrete `TunnelSocket` (a node Duplex) is worker code and shared stays browser-safe.
 */
export interface TunnelBroker<TSocket = unknown> {
  start(): Promise<void>
  stop(): Promise<void>
  openStream(hostId: string, opts?: { class?: TunnelStreamClass }): Promise<TSocket>
  /** An approved control connection is live. */
  isConnected(hostId: string): boolean
  /** Connected && approved && not 'updating'. */
  isAvailable(hostId: string): boolean
  activeStreams(hostId: string): number
  disconnect(hostId: string, code: number, reason: string): void
  /** Re-read the row now (pg-boss job `sandbox-host.agent-changed`). */
  refresh(hostId: string): Promise<void>
}

/** API-side enrollment service (implemented in packages/api/src/services/host-install.service.ts). */
export interface HostInstallService<TActor = string, TRequest = unknown> {
  /** New token for the host (replaces the previous one); plaintext only in the result. */
  createInstallToken(hostId: string): Promise<{ token: string; expiresAt: Date }>
  /** Registers the fetch, rotates the nonce and returns the rendered script (or the invalid-token script). */
  renderInstallScript(token: string, origin: string, ip: string | null): Promise<string>
  register(hostId: string, nonce: string, facts: AgentRegisterFacts, ip: string | null): Promise<AgentRegisterResponse | null>
  approveAgent(hostId: string, actor: TActor, enrolledAt: string): Promise<'approved' | 'enrollment_changed'>
  revokeAgent(hostId: string, actor: TActor, reason: 'revoked' | 'rejected' | 'removed'): Promise<void>
  /** §0 #13. Throws or returns null when the origin cannot be determined (409 public_url_not_configured). */
  resolveInstallOrigin(request: TRequest): string | null
}

// ---------------------------------------------------------------------------
// Machine-readable contract
// ---------------------------------------------------------------------------

/** The content of tunnel-contract.json. tunnel.test.ts fails when the file drifts from this; Go tests read the file. */
export function buildTunnelContract() {
  return {
    protocolVersion: TUNNEL_PROTOCOL_VERSION,
    authScheme: TUNNEL_AUTH_SCHEME,
    closeCodes: { ...TUNNEL_CLOSE_CODES },
    headers: { ...TUNNEL_HEADERS },
    paths: { ...TUNNEL_PATHS },
    dockerSocket: AGENT_DOCKER_SOCKET,
    hostPaths: { ...AGENT_HOST_PATHS },
    envKeys: { ...AGENT_ENV_KEYS },
    patterns: { ...TUNNEL_PATTERNS },
    prefixes: { installToken: INSTALL_TOKEN_PREFIX, nonce: INSTALL_NONCE_PREFIX, secret: AGENT_SECRET_PREFIX },
    messageTypes: { server: [...TUNNEL_SERVER_MESSAGE_TYPES], agent: [...TUNNEL_AGENT_MESSAGE_TYPES] },
    eofFrame: TUNNEL_EOF_FRAME,
    errorUpdating: TUNNEL_ERROR_UPDATING,
    arches: [...AGENT_ARCHES],
    updateReasons: [...AGENT_UPDATE_REASONS],
    helloUpdateStates: [...AGENT_HELLO_UPDATE_STATES],
    streamClasses: [...TUNNEL_STREAM_CLASSES],
    intervals: { ...TUNNEL_INTERVALS },
    limits: { ...TUNNEL_LIMITS },
  }
}
